// React libraries
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// PropTypes
import PropTypes from 'prop-types';

// Joy mui components
import IconButton from '@mui/joy/IconButton';
import Menu from '@mui/joy/Menu';
import MenuItem from '@mui/joy/MenuItem';
import ListDivider from '@mui/joy/ListDivider';
import MenuButton from '@mui/joy/MenuButton';
import Dropdown from '@mui/joy/Dropdown';

// Local components
import SpanTag from '../FormFields/SpanTag';
import EventSharePopup from './EventSharePopup';
import SnackBar from '../FormFields/Snackbar';

// Image
import LogoShadow from '../../assets/images/Shmooz (M) LOGO-06 1.png';

// Typography
import { H3, Body2 } from '../../themes/typography';

// Buttons
import { PrimaryBtn } from '../Buttons/Buttons';

// Icons
import { MaterialShareIcon, LikeIcon, ReportIcon, LikeBorderIcon } from '../../icons/MaterialIcon';
import {
    EllipsisVerticalIcon,
    CalendarIcon,
    LocationIcon,
    ClockIcon,
    DurationIcon,
    TicketIcon
} from '../../icons/FontAwesomeIcon';

// CSS file
import '../../styles/components/EventDetailBanner.css';

const eventImage =
    'https://img.freepik.com/free-photo/people-with-gothic-style-costumes-make-up-performing-drama-world-theatre-day_23-2151182206.jpg?t=st=1726553988~exp=1726557588~hmac=9d59aed4c96746fdf67f12e3f570dc30f451ef0bd758337f82f4559224e57711&w=740';

export default function EventDetailBanner({
    eventThumbnailUrl = eventImage,
    eventName = 'Summer music Festival Concert 2025',
    eventLocation = '52 San Adres New Your City, London, USA',
    eventCategorySubcategoryFormat = <SpanTag eventFormatTag="Travel" />,
    eventGetTicketBtn = <PrimaryBtn name="Get Ticket" />,
    eventDate = 'Tue, 05 Nov 2024',
    eventTimeSlot = '10:00 AM - 10:50 AM',
    eventTicketPrice,
    eventDuration = '50 Mins',
    numberOfTickets,
    orderId,
    showElipsis = true
    // eventTicketStatus
}) {
    const [openEventSharePopup, setOpenEventSharePopup] = useState(false); // State to handle EventSharePopup component open and close
    const [openSnackbar, setOpenSnackbar] = useState(false); // State to handle SnackBar component open and close
    const [isInterested, setIsInterested] = useState(false); // State to handle interest click event to show interested or interest

    // Function that opens EventSharePopup component
    function handleOpenEventSharePopup() {
        setOpenEventSharePopup(true);
    }

    // Function that closes of EventSharePopup component
    function handleCloseEventSharePopup() {
        setOpenEventSharePopup(false);
    }

    // send this value to backend if required to show interest status of user in this event
    // const interestStatus = isInterested
    // console.log('Interest Status:', interestStatus);

    // Function to handle interest click event set selected to no selected and non selected to selected
    function handleInterestClick() {
        setIsInterested(!isInterested); // Toggle interest state on click event
        setOpenSnackbar(true); // Show snackbar on click
    }

    // Conditional message to show in snackbar based on interest click event
    const snackbarMessage = isInterested
        ? 'Thank you for showing interest in this event!'
        : 'You have removed your interest in this event.';

    return (
        <React.Fragment>
            <div className="event-detail-banner-container">
                {/* Event Main Banner Section */}
                <div className="event-detail-banner-section">
                    {/* Event Thumbnail */}
                    <div className="event-meta-detail-thumbnail-image">
                        <img src={eventThumbnailUrl} alt="Event Thumbnail" />
                    </div>

                    {/* Event Details */}
                    <div className="event-meta-info-detail-container">
                        {/* Event Title */}
                        <div className="event-title-detail-section">
                            <H3 color="#F8A21E" fontWeight={700}>
                                {eventName}
                            </H3>
                        </div>

                        {/* Event Info Details */}
                        <div className="event-meta-information-section">
                            <div className="main-event-content-flex-section">
                                {/* Event Left side Details */}
                                <div className="event-meta-left-side-section">
                                    <div className="event-location-format-price-details-section">
                                        {/* Event Location */}
                                        <div className="event-meta-detail-location">
                                            <LocationIcon
                                                style={{
                                                    color: '#26C1E8',
                                                    fontSize: '1rem',
                                                    marginTop: '2px'
                                                }}
                                            />
                                            <Body2>{eventLocation}</Body2>
                                        </div>

                                        {/* Event Category, Sub-category, format */}
                                        <div className="event-meta-detail-tags-section">
                                            {eventCategorySubcategoryFormat}
                                        </div>

                                        {/* Event Pricing */}
                                        {eventTicketPrice && (
                                            <div className="event-meta-detail-pricing-section">
                                                {eventTicketPrice}
                                            </div>
                                        )}

                                        {orderId && (
                                            <div className="order-id-container">
                                                <Body2>Order ID: {orderId}</Body2>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Event Right side Details */}
                                <div className="event-meta-right-side-section">
                                    {/* Event Date */}
                                    <div className="event-meta-date-detail-section">
                                        <CalendarIcon
                                            style={{
                                                color: '#26C1E8',
                                                fontSize: '1rem',
                                                marginTop: '2px'
                                            }}
                                        />
                                        <Body2>{eventDate}</Body2>
                                    </div>

                                    {/* Condtionally render Event Time if prop is provided when reuse this event banner */}
                                    {eventTimeSlot && (
                                        <div className="event-meta-date-detail-section">
                                            <ClockIcon
                                                style={{
                                                    color: '#26C1E8',
                                                    fontSize: '1rem',
                                                    marginTop: '2px'
                                                }}
                                            />
                                            <Body2>{eventTimeSlot}</Body2>
                                        </div>
                                    )}

                                    {/* Will have to count duration based on startTime and endTime of the event. startDate and endDate is required when we calculate duration. So there will be 2 parameters should be pass in function. 1. startTime and endTime. 2. startDate and endDate */}
                                    {eventDuration && (
                                        <div className="event-meta-date-detail-section">
                                            <DurationIcon
                                                style={{
                                                    color: '#26C1E8',
                                                    fontSize: '1rem',
                                                    marginTop: '2px'
                                                }}
                                            />
                                            <Body2>{eventDuration}</Body2>
                                        </div>
                                    )}

                                    {/* Condtionally render Event Multiple Date  if prop is provided when reuse this event banner*/}
                                    {numberOfTickets && (
                                        <div className="event-meta-date-detail-section">
                                            <TicketIcon
                                                style={{
                                                    color: '#26C1E8',
                                                    fontSize: '1rem',
                                                    marginTop: '2px'
                                                }}
                                            />
                                            <Body2>Number of Tickets: {numberOfTickets}</Body2>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Event Get Ticket */}
                            <div className="event-meta-detail-get-ticket-section">
                                <div className="get-ticket-button-section">{eventGetTicketBtn}</div>
                                {/* <div className="get-ticket-link-section">

                                    {eventTicketStatus}
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {/* Shmooz Shadow Image*/}
                    <div className="event-meta-detail-shmooz-shadow">
                        {showElipsis && (
                            <div className="elipsis-icon">
                                {/* Drop down to show 3 options */}
                                <Dropdown>
                                    <MenuButton
                                        slots={{ root: IconButton }}
                                        slotProps={{
                                            root: { variant: 'outlined', color: 'neutral' }
                                        }}
                                        sx={{
                                            border: 'none',
                                            '&:hover': {
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                    >
                                        <EllipsisVerticalIcon
                                            style={{ color: 'white', fontSize: '1.7rem' }}
                                        />
                                    </MenuButton>

                                    {/* Starting point of option */}
                                    <Menu
                                        placement="bottom-end"
                                        sx={{
                                            backgroundColor: '#F5F5F5',
                                            borderRadius: '15px',
                                            border: 'none',
                                            height: 'auto',
                                            padding: '12px 0px 10px 0px',
                                            justifyContent: 'flex-start',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        {/* Interest Option */}
                                        <MenuItem onClick={handleInterestClick}>
                                            {isInterested ? (
                                                <LikeIcon
                                                    style={{ color: '#26C1E8', fontSize: '1.5rem' }}
                                                />
                                            ) : (
                                                <LikeBorderIcon
                                                    style={{ color: '#26C1E8', fontSize: '1.5rem' }}
                                                />
                                            )}
                                            <Body2 color="#11203A">
                                                {isInterested ? 'Interested' : 'Interest'}
                                            </Body2>
                                        </MenuItem>
                                        <ListDivider />

                                        {/* Share Option */}
                                        <MenuItem onClick={handleOpenEventSharePopup}>
                                            <MaterialShareIcon
                                                style={{ color: '#26C1E8', fontSize: '1.5rem' }}
                                            />
                                            <Body2 color="#11203A">Share</Body2>
                                        </MenuItem>
                                        <ListDivider />

                                        {/* Report an issue Option */}
                                        <MenuItem>
                                            <ReportIcon
                                                style={{ color: '#26C1E8', fontSize: '1.5rem' }}
                                            />
                                            <Link to="/report-event" target="_blank">
                                                <Body2 color="#11203A">Report an issue</Body2>
                                            </Link>
                                        </MenuItem>
                                    </Menu>

                                    {/* Event Share Popup that open when user clicks on Share option */}
                                    <EventSharePopup
                                        open={openEventSharePopup}
                                        onClose={handleCloseEventSharePopup}
                                        shareEventName={eventName}
                                    />
                                </Dropdown>
                            </div>
                        )}

                        {/* Shmooz Logo Section */}
                        <div className="event-meta-logo-shadow-image">
                            <img src={LogoShadow} alt="Shmooz Logo" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Snackbar to show conditional interest and interested message */}
            <SnackBar
                openSnack={openSnackbar}
                setOpenSnack={setOpenSnackbar}
                message={snackbarMessage}
            />
        </React.Fragment>
    );
}

// PropTypes Validation
EventDetailBanner.propTypes = {
    eventTicketPrice: PropTypes.number || PropTypes.string,
    eventGetTicketBtn: PropTypes.element,
    eventThumbnailUrl: PropTypes.string,
    eventName: PropTypes.string,
    eventLocation: PropTypes.string,
    eventCategorySubcategoryFormat: PropTypes.element,
    eventDate: PropTypes.string,
    eventTimeSlot: PropTypes.string,
    eventDuration: PropTypes.string,
    numberOfTickets: PropTypes.number || PropTypes.string,
    orderId: PropTypes.number || PropTypes.string
};
